import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import UltimatePagination from 'react-ultimate-pagination-bootstrap-3';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle, faSearch } from '@fortawesome/pro-solid-svg-icons';
import { faBuilding } from '@fortawesome/pro-regular-svg-icons';

import CompaniesAddToCallListModal from './modals/add_to_call_list_modal';
import CompaniesNewCallListModal from './modals/new_call_list_modal';
import CompaniesBulkTaggingModal from './modals/companies_bulk_tagging_modal';
import CompaniesDeleteGroupModal from './modals/companies_delete_group_modal';
import CompaniesGroupFormModal from './modals/companies_group_form_modal';
import CompaniesBulkDeleteModal from './modals/companies_bulk_delete_modal';

import CompaniesTable from './companies_table';
import CompaniesFilterTopbar from './companies_filter_topbar';
import MobileContactFilters from '../filters/mobile_contact_filters';
import DuplicateCompaniesView from './duplicates/duplicate_companies_view';

import CompanyActionsDropdown from './company_actions_dropdown';
import LoadingSpinner from 'components/loading_spinner';
import PageHeader from 'components/page_header';

import {
  getTasks,
  retrieveGroups
} from '../store/actions/companies';

import { changeCompanyPage } from '../store/actions/query';
import { retrieveCompanies, delayRetrieveRecords, downloadCompaniesAsCsv } from '../store/actions/search';
import { isDuplicateViewActive } from '../store/selectors/duplicates';

export class CompaniesTab extends React.Component {
  static propTypes = {
    canExport: PropTypes.bool,
    companies: PropTypes.array.isRequired,
    currentPage: PropTypes.number.isRequired,
    delayRetrieveRecords: PropTypes.func,
    downloadCompaniesAsCsv: PropTypes.func.isRequired,
    emptyStateHelpPath: PropTypes.string.isRequired,
    isDuplicateViewActive: PropTypes.bool,
    isFetchingCompanies: PropTypes.bool,
    mobileFiltersVisible: PropTypes.bool,
    newCompanyPath: PropTypes.string.isRequired,
    pageSize: PropTypes.number.isRequired,
    retrieveCompanies: PropTypes.func.isRequired,
    retrieveGroups: PropTypes.func.isRequired,
    svnMember: PropTypes.bool,
    totalCount: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,

    onPageChange: PropTypes.func.isRequired
  };

  scrollPanel = React.createRef();

  componentDidMount() {
    const { retrieveGroups } = this.props;
    retrieveGroups();
  }

  handlePageChange = (page) => {
    const { onPageChange, retrieveCompanies } = this.props;
    onPageChange(page);
    retrieveCompanies();
    this.scrollToTop();
  };

  scrollToTop() {
    this.scrollPanel.current.scrollTop = 0;
  }

  getCompany(id) {
    const { companies } = this.props;
    return companies.find(company => (
      company.id === id
    ));
  }

  render() {
    const { isDuplicateViewActive, mobileFiltersVisible } = this.props;

    if (isDuplicateViewActive) {
      return <DuplicateCompaniesView />;
    }

    return (
      <React.Fragment>
        {this.renderPageHeader()}
        {this.renderFilterTopBar()}
        <div className="table-content">
          <div className="main-content" ref={this.scrollPanel}>
            {this.renderResults()}
            {this.renderPagination()}
          </div>
        </div>
        {mobileFiltersVisible && this.renderMobileFilters()}
        <CompaniesAddToCallListModal />
        <CompaniesNewCallListModal />
        <CompaniesDeleteGroupModal />
        <CompaniesGroupFormModal />
        <CompaniesBulkTaggingModal />
        <CompaniesBulkDeleteModal />
      </React.Fragment>
    );
  }

  renderMobileFilters() {
    return (
      <div className="visible-xs visible-sm mobile-content">
        <MobileContactFilters isCompany />
      </div>
    );
  }

  renderPageHeader() {
    const { svnMember, newCompanyPath } = this.props;

    return (
      <PageHeader
        iconDescription="Manage your company database"
        iconName={faBuilding}
        iconSize="lg"
        iconText="Companies"
        iconType="icon"
      >
        <div className="top-bar">
          <div className="d-inline-block hidden-xs hidden-sm">
            { svnMember &&
              <a
                className="mr-4
                visible-lg-inline"
                href="https://support.buildout.com/hc/en-us/articles/360045428854-Companies"
                rel="noopener noreferrer"
                target="_blank"
              >
                <FontAwesomeIcon icon={faQuestionCircle} />
                <span className="ml-2">How to use your Company Database</span>
              </a>
            }
            <CompanyActionsDropdown />
          </div>
          {this.renderCSVButton()}
          <a className="btn btn-create" href={newCompanyPath}>
            New Company
          </a>
        </div>
      </PageHeader>
    );
  }

  renderCSVButton() {
    const { canExport, downloadCompaniesAsCsv } = this.props;
    if (canExport) {
      return (
        <button className="btn btn-dark right-buffer-md hidden-xs hidden-sm" onClick={downloadCompaniesAsCsv}>
          Export as CSV
        </button>
      );
    }
  }

  renderFilterTopBar() {
    const {
      companies,
      currentPage,
      pageSize,
      totalCount,
    } = this.props;

    const pageData = {
      currentPage: currentPage,
      itemCount: companies.length,
      itemSingularLabel: 'company',
      pageSize: pageSize,
      totalCount: totalCount
    };

    return (
      <CompaniesFilterTopbar pageData={pageData} />
    );
  }

  renderPagination() {
    const { companies, totalCount, currentPage, totalPages } = this.props;

    if (companies.length < totalCount) {
      return (
        <div className="text-center">
          <UltimatePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onChange={this.handlePageChange}
          />
        </div>
      );
    }
  }

  renderResults() {
    const {
      companies,
      emptyStateHelpPath,
      isFetchingCompanies,
      svnMember
    } = this.props;

    if (isFetchingCompanies) {
      return <LoadingSpinner />;
    } else if (companies.length > 0) {
      return (
        <CompaniesTable />
      );
    } else {
      return (
        <div className="no-results-panel">
          <FontAwesomeIcon className="text-muted mt-4"  icon={faSearch} size="5x"/>
          <h4 className="mt-4">
            <strong className="text-lg">No results found</strong>
          </h4>
          <p className="text-muted mb-4">Search with different criteria or create a New Company</p>
          {svnMember &&
            <a className="mt-4" href={emptyStateHelpPath}>
              <FontAwesomeIcon icon={faQuestionCircle} />
              <span className="ml-2">How to use your Company Database</span>
            </a>
          }
        </div>
      );
    }
  }
}

const mapStateToProps = state => ({
  ...state.companies,
  ...state.search.meta.companies,
  canExport: state.ui.canExport,
  companies: state.search.companies,
  emptyStateHelpPath: state.paths.emptyStateHelp,
  isDuplicateViewActive: isDuplicateViewActive(state),
  isFetchingCompanies: state.search.isFetching,
  newCompanyPath: state.paths.companies.newCompany,
  mobileFiltersVisible: state.ui.mobileFiltersVisible,
  svnMember: state.ui.svnMember,
});

const mapDispatchToProps = {
  delayRetrieveRecords,
  downloadCompaniesAsCsv,
  getTasks,
  retrieveGroups,
  retrieveCompanies,

  onPageChange: changeCompanyPage,
};

export default connect(mapStateToProps, mapDispatchToProps)(CompaniesTab);
